// Pure selection logic for the Rechnungsversand step: which of a saved booking's
// invoice positions are carried over to a new Beleg (Rechnung/Quittung).
//
// Mirrors the legacy dialog's exactness (form-widget-positions:417 passes the
// real ticked position objects): each ticked row claims AT MOST ONE position, so
// a product that ends up on several positions never drags the whole tab onto the
// Beleg. The step lists every billable source — one-off services, the
// Beziehung's Leistungen, subscriptions (first period; the scheduler stamps
// that position with this booking's id at save time), key deposits, and flat-rate
// disconnected electric connections. The only positions kept OUT are
// already-settled ones; electric positions match only a ticked electric row.
//
// Side-effect free (no facade/HTTP) → trivially unit testable.

// The subset of an InvoicePosition this module reads. A full @shared
// InvoicePosition satisfies it structurally; tests build plain literals.
export interface BelegCandidatePosition {
    id: number;
    title: string;
    // Already settled on an invoice/receipt → never offered again.
    isPaid?: boolean;
    product?: { id: number } | null;
    // Electric-meter fee line — billed via the power tab, not shown in the step.
    bcm_electric_meter_has_boats_id?: number | null;
}

// A ticked Rechnungsversand row: a catalog row (service / Beziehung /
// subscription / deposit) carries its productId, an ad-hoc row carries only its
// name. `source` keeps electric rows matching electric positions only — see below.
export interface BelegSelectionItem {
    source?: string;
    productId: number | null;
    name: string;
}

// The positions to carry onto the new Beleg: for each ticked row, the first
// still-unclaimed position matching it — catalog rows by product id, ad-hoc rows
// by title. At most one position per row (no over-matching) and never the same
// position twice. Already-paid positions are never eligible. Electric-meter
// positions are eligible ONLY for a ticked electric (flat-rate, disconnected) row,
// and a non-electric row never claims a meter position — so a product id shared
// between a Stromgebühr and a normal service can't cross-match.
export function selectBelegPositions<T extends BelegCandidatePosition>(
    open: readonly T[],
    selection: readonly BelegSelectionItem[],
): T[] {
    if (!selection.length) {
        return [];
    }

    const candidates = open.filter(p => !p.isPaid);
    const claimed = new Set<number>();
    const result: T[] = [];

    for (const item of selection) {
        const wantsElectric = item.source === 'electric';
        const match = candidates.find(p => {
            if (claimed.has(p.id)) {
                return false;
            }
            // Electric and non-electric rows never claim each other's positions.
            if (wantsElectric !== !!p.bcm_electric_meter_has_boats_id) {
                return false;
            }
            return item.productId != null
                ? p.product?.id === item.productId
                : p.title === item.name;
        });
        if (match) {
            claimed.add(match.id);
            result.push(match);
        }
    }

    return result;
}
